import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Network, Loader2, Check, ArrowRight, ArrowLeft, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

const NIGERIAN_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT - Abuja','Gombe',
  'Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos',
  'Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto',
  'Taraba','Yobe','Zamfara',
];

export default function PartnerRegister() {
  const [step, setStep] = useState(1);
  const [programType, setProgramType] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [doneData, setDoneData] = useState(null);
  const [searchParams] = useSearchParams();
  const { register } = usePartnerAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: searchParams.get('email') || '',
    phone: '', password: '', confirm_password: '',
    business_name: '', state: '', city: '',
    proposed_territory: '', sales_experience: '', why_reseller: '',
    expected_schools_per_month: 5,
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        program_type: programType,
        country: 'Nigeria',
      };
      delete payload.confirm_password;
      const res = await register(payload);
      setDone(true);
      setDoneData(res);
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (done && doneData) {
    const isReferral = programType === 'referral';
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900 mb-2">
            {isReferral ? "You're in!" : 'Application Submitted!'}
          </h1>
          <p className="text-slate-500 text-sm mb-6">
            {isReferral
              ? 'Your referral account is active. Start sharing your link below.'
              : 'Your reseller application is under review. We\'ll notify you within 48 hours. Meanwhile, your referral link is active!'}
          </p>
          {doneData.partner?.referral_url && (
            <div className="bg-white border border-slate-100 rounded-2xl p-4 mb-6 text-left">
              <p className="text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Your Referral Link
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[#516EFA] truncate">
                  {doneData.partner.referral_url}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(doneData.partner.referral_url);
                    toast.success('Copied!');
                  }}
                  className="text-xs bg-[#516EFA] text-white px-3 py-2 rounded-lg hover:bg-[#425BE0] transition whitespace-nowrap"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/reseller/dashboard')}
            className="w-full bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg py-2.5 text-sm font-medium transition-colors"
            data-testid="partner-register-goto-dashboard"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 bg-[#516EFA] rounded-xl flex items-center justify-center">
              <Network className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="font-manrope font-bold text-slate-900 text-base leading-tight">EDUVA</p>
              <p className="text-xs text-[#516EFA] font-medium">Partner Network</p>
            </div>
          </div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Join the Network</h1>
          <p className="text-sm text-slate-500 mt-1">
            {step === 1 ? 'Choose how you want to partner with EDUVA'
              : step === 2 ? 'Create your partner account'
              : 'Tell us about your business'}
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, ...(programType === 'reseller' ? [3] : [])].map((s) => (
            <div key={s} className={`flex items-center gap-1 ${s < step ? 'text-emerald-600' : s === step ? 'text-[#516EFA]' : 'text-slate-300'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                s < step ? 'border-emerald-500 bg-emerald-50' : s === step ? 'border-[#516EFA] bg-[#EDF0FF]' : 'border-slate-200 bg-white'
              }`}>
                {s < step ? <Check className="w-3 h-3" /> : s}
              </div>
              {s < (programType === 'reseller' ? 3 : 2) && (
                <div className={`w-8 h-0.5 ${s < step ? 'bg-emerald-400' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
          {/* Step 1: Choose program type */}
          {step === 1 && (
            <div className="space-y-3">
              {[
                {
                  type: 'referral',
                  icon: Users,
                  title: 'Referral Partner',
                  desc: 'Share your unique link. Earn commission when schools you refer subscribe.',
                  badge: 'Instant activation',
                  badgeColor: 'bg-emerald-100 text-emerald-700',
                },
                {
                  type: 'reseller',
                  icon: Building2,
                  title: 'Reseller',
                  desc: 'Actively sell EDUVA in your territory. Higher commissions, dedicated support.',
                  badge: 'Application required',
                  badgeColor: 'bg-amber-100 text-amber-700',
                },
              ].map(({ type, icon: Icon, title, desc, badge, badgeColor }) => (
                <button
                  key={type}
                  onClick={() => { setProgramType(type); setStep(2); }}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all hover:border-[#516EFA] hover:bg-[#EDF0FF]/30 ${
                    programType === type ? 'border-[#516EFA] bg-[#EDF0FF]/30' : 'border-slate-200'
                  }`}
                  data-testid={`partner-type-${type}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#516EFA]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-slate-900 text-sm">{title}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badgeColor}`}>{badge}</span>
                      </div>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300 self-center flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Personal info */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">First Name</label>
                  <input
                    value={form.first_name}
                    onChange={(e) => update('first_name', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                    required
                    data-testid="partner-reg-firstname"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Last Name</label>
                  <input
                    value={form.last_name}
                    onChange={(e) => update('last_name', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                    required
                    data-testid="partner-reg-lastname"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                  required
                  data-testid="partner-reg-email"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="08012345678"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                  required
                  data-testid="partner-reg-phone"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                  required
                  data-testid="partner-reg-password"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Confirm Password</label>
                <input
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => update('confirm_password', e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                  required
                  data-testid="partner-reg-confirm-password"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => {
                    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.password) {
                      toast.error('Please fill in all fields');
                      return;
                    }
                    if (form.password !== form.confirm_password) {
                      toast.error('Passwords do not match');
                      return;
                    }
                    if (programType === 'reseller') { setStep(3); } else { handleSubmit(); }
                  }}
                  disabled={loading}
                  className="flex-1 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg py-2.5 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="partner-reg-next"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {programType === 'reseller' ? 'Next' : (loading ? 'Creating...' : 'Create Account')}
                  {programType === 'reseller' && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Reseller info */}
          {step === 3 && programType === 'reseller' && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Business Name</label>
                <input
                  value={form.business_name}
                  onChange={(e) => update('business_name', e.target.value)}
                  placeholder="Your company / trade name"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                  data-testid="partner-reg-business"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">State</label>
                  <select
                    value={form.state}
                    onChange={(e) => update('state', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none bg-white"
                    data-testid="partner-reg-state"
                  >
                    <option value="">Select state</option>
                    {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">City</label>
                  <input
                    value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                    data-testid="partner-reg-city"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Proposed Territory (states/regions)</label>
                <input
                  value={form.proposed_territory}
                  onChange={(e) => update('proposed_territory', e.target.value)}
                  placeholder="e.g. Lagos, Ogun"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
                  data-testid="partner-reg-territory"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Sales Experience</label>
                <textarea
                  value={form.sales_experience}
                  onChange={(e) => update('sales_experience', e.target.value)}
                  placeholder="Describe your sales background..."
                  rows={2}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none resize-none"
                  data-testid="partner-reg-experience"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Why do you want to become an EDUVA reseller?</label>
                <textarea
                  value={form.why_reseller}
                  onChange={(e) => update('why_reseller', e.target.value)}
                  rows={2}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none resize-none"
                  data-testid="partner-reg-why"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg py-2.5 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="partner-reg-submit"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-4">
          Already a partner?{' '}
          <Link to="/reseller/login" className="text-[#516EFA] hover:text-[#425BE0] font-medium">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
